'use client';

import { Link, usePathname } from '@/i18n/routing';
import { useRouter as useNextRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { Suspense, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getClient } from '@/lib/supabase/client';
import { useLocale, useTranslations } from 'next-intl';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Bell,
  Brush,
  ChevronDown,
  Clock,
  Contact,
  FileText,
  ImageIcon,
  PanelsTopLeft,
  Images,
  LogOut,
  Mail,
  Moon,
  Settings,
  ShieldCheck,
  Sparkles,
  StretchHorizontal,
  Type,
  User,
  UserPlus,
  WandSparkles,
  Zap,
} from 'lucide-react';
import AuthModal from '@/components/AuthModal';
import PricingModal from '@/components/PricingModal';
import SettingsModal from '@/components/SettingsModal';
import InviteFriendsModal from '@/components/InviteFriendsModal';
import GoogleOneTap from '@/components/GoogleOneTap';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import SiteAnnouncementBar from '@/components/SiteAnnouncementBar';
import PersonalizedOfferModal from '@/components/PersonalizedOfferModal';
import { fetchCachedApi, CLIENT_CACHE_KEYS, CLIENT_CACHE_EVENTS } from '@/lib/client-request-cache';
import {
  AI_IMAGE_FEATURE_MENU,
  AI_VIDEO_FEATURE_MENU,
  HEADER_VIDEO_MODEL_MENU,
  IMAGE_MODEL_MENU,
  MODEL_MENU_BADGE_STYLES,
} from '@/config/model-navigation';
import { useCampaignConfigs } from '@/hooks/useCampaignConfigs';
import NotificationPanel from '@/components/social/notifications/NotificationPanel';
import { useSharedNotificationCenter } from '@/components/social/notifications/NotificationCenterProvider';
import { AUTH_REQUIRED_EVENT } from '@/lib/auth/client-auth';
import { PROFILE_UPDATED_EVENT } from '@/lib/profile-events';
import { resolveProfileAvatarSrc } from '@/lib/profile-avatar-src';
import ProfileAvatar from '@/components/profile/ProfileAvatar';
import BrandLockup from '@/components/BrandLockup';

// Phase 1 UX closeout: 顶部导航中的 Explore / Create 由运行时 API 开关控制，
// 服务端布局注入的 bootstrap 值优先，避免首屏闪烁。
const SOCIAL_NAV_BOOTSTRAP_KEY = '__BOTGO_SOCIAL_NAV_ENABLED__';

const MENU_ITEM_ICONS = {
  brush: Brush,
  edit: WandSparkles,
  extend: StretchHorizontal,
  frames: PanelsTopLeft,
  image: ImageIcon,
  images: Images,
  sparkles: Sparkles,
  type: Type,
};

const MEGA_MENU_MAX_WIDTH = 960;
const MEGA_MENU_VIEWPORT_GUTTER = 16;

const getMegaMenuLeft = (triggerElement) => {
  const panelWidth = Math.min(
    MEGA_MENU_MAX_WIDTH,
    window.innerWidth - (MEGA_MENU_VIEWPORT_GUTTER * 2)
  );
  const maxLeft = window.innerWidth - panelWidth - MEGA_MENU_VIEWPORT_GUTTER;
  const triggerLeft = triggerElement.getBoundingClientRect().left;

  return Math.min(
    Math.max(MEGA_MENU_VIEWPORT_GUTTER, triggerLeft),
    maxLeft
  );
};

function HeaderUrlModals({
  supabase,
  setIsAuthModalOpen,
  setRedirectPath,
  setSettingsInitialTab,
  setIsSettingsModalOpen,
}) {
  const searchParams = useSearchParams();
  const router = useNextRouter();

  // 检测 URL 参数，自动打开 AuthModal 或 SettingsModal
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    const authParam = params.get('auth');
    const redirectParam = params.get('redirect');
    const settingsParam = params.get('settings');
    let shouldReplaceUrl = false;

    if (authParam === 'login') {
      setIsAuthModalOpen(true);
      if (redirectParam) {
        setRedirectPath(redirectParam);
      }
      params.delete('auth');
      params.delete('redirect');
      shouldReplaceUrl = true;
    }

    if (settingsParam) {
      const normalizedTab = ['account', 'usage', 'billing'].includes(settingsParam)
        ? settingsParam
        : 'account';
      void supabase.auth.getUser().then(({ data }) => {
        if (data?.user) {
          setSettingsInitialTab(normalizedTab);
          setIsSettingsModalOpen(true);
          return;
        }
        setRedirectPath(`${window.location.pathname}?settings=${normalizedTab}`);
        setIsAuthModalOpen(true);
      });
      params.delete('settings');
      shouldReplaceUrl = true;
    }

    if (shouldReplaceUrl) {
      const newQuery = params.toString();
      const newUrl = newQuery
        ? `${window.location.pathname}?${newQuery}`
        : window.location.pathname;
      // Update the router's query state as well as the address bar, so the same
      // login redirect can trigger again after the modal has been dismissed.
      router.replace(newUrl, { scroll: false });
    }
  }, [supabase.auth, searchParams, router, setIsAuthModalOpen, setRedirectPath, setSettingsInitialTab, setIsSettingsModalOpen]);

  return null;
}

export default function Header() {
  const t = useTranslations('Header');
  const locale = useLocale();
  const pathname = usePathname();
  const {
    siteAnnouncement,
    personalizedOffer,
  } = useCampaignConfigs();
  const [user, setUser] = useState(null);
  const [databaseProfile, setDatabaseProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // 添加加载状态，默认为 true
  const [credits, setCredits] = useState(0); // 旧字段，保留兼容性
  const [creditDetails, setCreditDetails] = useState({
    freeDailyCredits: 0,
    monthlyCredits: 0,
    permanentCredits: 0,
    totalAvailable: 0,
    freeDailyCreditsResetAt: null,
  });
  const [planName, setPlanName] = useState('Free');
  const [endDate, setEndDate] = useState(null);
  const [nextMonthlyReset, setNextMonthlyReset] = useState(null);  // ✅ 新增：下次月度积分重置时间
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isUpgradeDropdownOpen, setIsUpgradeDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [expandedUserMenuSection, setExpandedUserMenuSection] = useState(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [megaMenuLeft, setMegaMenuLeft] = useState(MEGA_MENU_VIEWPORT_GUTTER);
  const megaMenuCloseTimerRef = useRef(null);
  const upgradeMenuCloseTimerRef = useRef(null);
  const userMenuCloseTimerRef = useRef(null);
  const upgradeMenuPointerRef = useRef(false);
  const userMenuPointerRef = useRef(false);
  const megaMenuScrollRef = useRef(null);
  const [megaMenuHasMore, setMegaMenuHasMore] = useState(false);
  const videoMenuTriggerRef = useRef(null);
  const imageMenuTriggerRef = useRef(null);
  const [settingsInitialTab, setSettingsInitialTab] = useState('account');
  const [redirectPath, setRedirectPath] = useState(null); // 登录后的重定向路径
  const supabase = useMemo(() => getClient(), []);
  // 首次客户端渲染必须与服务端保持一致；bootstrap 值在挂载后立即应用，
  // 避免 Explore/Create 与 AI Video/Image 两套导航发生 hydration mismatch。
  const [socialNavEnabled, setSocialNavEnabled] = useState(false);
  const notifications = useSharedNotificationCenter();
  const notificationsOpen = notifications.open;
  const setNotificationsOpen = notifications.setOpen;

  useEffect(() => {
    const openAuth = (event) => {
      event.preventDefault();
      setRedirectPath(event.detail?.redirectPath || pathname || '/');
      setIsAuthModalOpen(true);
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, openAuth);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, openAuth);
  }, [pathname]);

  useEffect(() => {
    const updateProfile = (event) => setDatabaseProfile(event.detail || null);
    window.addEventListener(PROFILE_UPDATED_EVENT, updateProfile);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, updateProfile);
  }, []);

  useEffect(() => {
    if (!isUserDropdownOpen) setExpandedUserMenuSection(null);
  }, [isUserDropdownOpen]);

  const openHoverMenu = (setOpen, timerRef) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    setOpen(true);
  };

  const closeHoverMenu = (setOpen, timerRef) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setOpen(false);
      timerRef.current = null;
    }, 140);
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && window[SOCIAL_NAV_BOOTSTRAP_KEY] === true) {
      setSocialNavEnabled(true);
      return undefined;
    }
    const controller = new AbortController();
    fetch('/api/social/nav-flags', {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return;
        const body = await response.json().catch(() => null);
        if (body?.enabled === true) setSocialNavEnabled(true);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const clearMegaMenuCloseTimer = () => {
    if (megaMenuCloseTimerRef.current) {
      window.clearTimeout(megaMenuCloseTimerRef.current);
      megaMenuCloseTimerRef.current = null;
    }
  };

  const openMegaMenu = (menu, triggerElement) => {
    clearMegaMenuCloseTimer();
    setMegaMenuLeft(getMegaMenuLeft(triggerElement));
    setActiveMegaMenu(menu);
  };

  const scheduleMegaMenuClose = () => {
    clearMegaMenuCloseTimer();
    megaMenuCloseTimerRef.current = window.setTimeout(() => {
      setActiveMegaMenu(null);
      megaMenuCloseTimerRef.current = null;
    }, 140);
  };

  // 检测 mega menu 滚动容器是否还有未显示内容，驱动底部渐隐遮罩的隐现
  const updateMegaMenuFade = useCallback(() => {
    const el = megaMenuScrollRef.current;
    if (!el) return;
    setMegaMenuHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, []);

  useEffect(() => () => {
    clearMegaMenuCloseTimer();
    if (upgradeMenuCloseTimerRef.current) window.clearTimeout(upgradeMenuCloseTimerRef.current);
    if (userMenuCloseTimerRef.current) window.clearTimeout(userMenuCloseTimerRef.current);
  }, []);

  // 菜单打开/切换时回到顶部并测量一次；窗口尺寸变化时重测
  useEffect(() => {
    setMegaMenuHasMore(false);
    if (!activeMegaMenu) return undefined;
    const el = megaMenuScrollRef.current;
    if (el) el.scrollTop = 0;
    const raf = window.requestAnimationFrame(updateMegaMenuFade);
    window.addEventListener('resize', updateMegaMenuFade);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateMegaMenuFade);
    };
  }, [activeMegaMenu, updateMegaMenuFade]);

  useEffect(() => {
    if (!activeMegaMenu) return undefined;

    const updateMegaMenuPosition = () => {
      const triggerElement = activeMegaMenu === 'video'
        ? videoMenuTriggerRef.current
        : imageMenuTriggerRef.current;

      if (triggerElement) {
        setMegaMenuLeft(getMegaMenuLeft(triggerElement));
      }
    };

    window.addEventListener('resize', updateMegaMenuPosition);
    return () => window.removeEventListener('resize', updateMegaMenuPosition);
  }, [activeMegaMenu]);

  const handleAnnouncementAction = () => {
    if (pathname === '/pricing') {
      const spotlightSection = document.getElementById('pricing-spotlight');
      if (spotlightSection) {
        const nextTop = spotlightSection.getBoundingClientRect().top + window.scrollY - 140;
        window.scrollTo({
          top: Math.max(nextTop, 0),
          behavior: 'smooth',
        });
      }
      return;
    }

    setIsPricingModalOpen(true);
  };

  // Keep header links responsive on the pricing page even when its client-side
  // route transition stalls after campaign or modal updates.
  const handleHeaderLinkClick = (event) => {
    if (pathname !== '/pricing' || event.defaultPrevented || event.button !== 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const destination = new URL(event.currentTarget.href, window.location.href);
    if (destination.origin !== window.location.origin || destination.href === window.location.href) return;
    event.preventDefault();
    window.location.assign(destination.href);
  };

  // 获取用户信息、积分和订阅信息
  useEffect(() => {
    const resetUserState = () => {
      setDatabaseProfile(null);
      setCredits(0);
      setCreditDetails({
        freeDailyCredits: 0,
        monthlyCredits: 0,
        permanentCredits: 0,
        totalAvailable: 0,
        freeDailyCreditsResetAt: null,
      });
      setPlanName('Free');
      setEndDate(null);
      setNextMonthlyReset(null);  // ✅ 新增：重置月度积分时间
    };

    async function fetchUser(overrideUser = null, { force = false } = {}) {
      try {
        const resolvedUser = overrideUser ?? (await supabase.auth.getUser()).data.user;
        setUser(resolvedUser);

        if (resolvedUser) {
          // 获取订阅信息（包含积分余额、套餐名称、到期时间）
          try {
            const [subscriptionRes, creditsRes, profilePayload] = await Promise.all([
              fetchCachedApi('/api/user/subscription', {
                cacheKey: CLIENT_CACHE_KEYS.subscription,
                ttlMs: 60000,
                force,
              }),
              fetchCachedApi('/api/credits', {
                cacheKey: CLIENT_CACHE_KEYS.credits,
                ttlMs: 30000,
                force,
              }),
              fetch('/api/user/profile', {
                credentials: 'same-origin',
                cache: 'no-store',
              })
                .then(async (response) => response.ok ? response.json() : null)
                .catch(() => null),
            ]);

            setDatabaseProfile(profilePayload?.user || null);

            // 处理订阅信息
            let fallbackTotalCredits = 0;
            if (subscriptionRes.ok) {
              const data = subscriptionRes.data || {};
              setPlanName(data.planName || 'Free');
              setEndDate(data.endDate ? new Date(data.endDate) : null);
              // ✅ 新增：设置下次月度积分重置时间
              setNextMonthlyReset(data.nextMonthlyReset ? new Date(data.nextMonthlyReset) : null);
              // 订阅接口 totalAvailable 仅包含付费积分，这里仅作为兜底
              fallbackTotalCredits = Number(data.totalAvailable) || 0;
            }

            // 处理积分详情
            if (creditsRes.ok) {
              const creditsData = creditsRes.data || {};
              const freeDailyCredits = Number(creditsData.freeDailyCredits) || 0;
              const monthlyCredits = Number(creditsData.monthlyCredits) || 0;
              const permanentCredits = Number(creditsData.permanentCredits) || 0;
              const totalCredits = freeDailyCredits + monthlyCredits + permanentCredits;

              setCreditDetails({
                freeDailyCredits,
                monthlyCredits,
                permanentCredits,
                totalAvailable: totalCredits,
                freeDailyCreditsResetAt: creditsData.freeDailyCreditsResetAt,
              });
              setCredits(totalCredits);
            } else {
              setCredits(fallbackTotalCredits);
            }
          } catch (error) {
            console.error('Failed to fetch user data:', error);
            resetUserState();
          }
        } else {
          resetUserState();
        }
      } catch (e) {
        console.error('Unexpected error in fetchUser:', e);
        resetUserState();
      } finally {
        setIsLoading(false);
      }
    }

    fetchUser();

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') return;
      if (!session?.user) {
        setUser(null);
        resetUserState();
        return;
      }
      fetchUser(session.user, {
        force: event === 'SIGNED_IN' || event === 'USER_UPDATED',
      });
    });

    const handleSubscriptionRefresh = () => {
      fetchUser(null, { force: true });
    };

    window.addEventListener(CLIENT_CACHE_EVENTS.subscriptionUpdated, handleSubscriptionRefresh);
    window.addEventListener(CLIENT_CACHE_EVENTS.creditsUpdated, handleSubscriptionRefresh);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(CLIENT_CACHE_EVENTS.subscriptionUpdated, handleSubscriptionRefresh);
      window.removeEventListener(CLIENT_CACHE_EVENTS.creditsUpdated, handleSubscriptionRefresh);
    };
  }, [supabase]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.assign(`/${locale}`);  // 退出登录后跳转到首页
  };

  const openUsageSettings = () => {
    setIsUpgradeDropdownOpen(false);
    setSettingsInitialTab('usage');
    setIsSettingsModalOpen(true);
  };

  const userMenuDisplayName = databaseProfile?.displayName
    || databaseProfile?.username
    || user?.user_metadata?.display_name
    || user?.user_metadata?.name
    || user?.email?.split('@')[0]
    || '';
  const userMenuAvatarUrl = databaseProfile?.avatarUrl || user?.user_metadata?.avatar_url || null;
  const userMenuAvatarSrc = resolveProfileAvatarSrc({
    id: databaseProfile?.id || user?.id,
    avatarUrl: userMenuAvatarUrl,
  });

  // 计算到期时间的文本显示（格式：xx days yy hours zz minutes）
  const getTimeUntilExpiry = (targetDate = endDate) => {
    if (!targetDate) return 'N/A';

    const now = new Date();
    const diff = targetDate.getTime() - now.getTime();

    if (diff <= 0) return t('expired');

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days} ${t('days')} ${hours} ${t('hours')} ${minutes} ${t('minutes')}`;
    } else if (hours > 0) {
      return `${hours} ${t('hours')} ${minutes} ${t('minutes')}`;
    } else {
      return `${minutes} ${t('minutes')}`;
    }
  };

  // 计算到每日0点的时间（格式：yy hours zz minutes）
  const getTimeUntilMidnight = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const diff = tomorrow.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours} ${t('hours')} ${minutes} ${t('minutes')}`;
  };

  const megaMenus = {
    video: {
      features: AI_VIDEO_FEATURE_MENU,
      models: HEADER_VIDEO_MODEL_MENU,
    },
    image: {
      features: AI_IMAGE_FEATURE_MENU,
      models: IMAGE_MODEL_MENU,
    },
  };

  const getMenuItemLabel = (item) => (
    item.translationKey
      ? t(`featureMenu.${item.translationKey}.label`)
      : item.labelKey ? t(`models.${item.labelKey}`) : item.label
  );

  const getMenuItemDescription = (item) => {
    const key = item.translationKey
      ? `featureMenu.${item.translationKey}.description`
      : `modelDescriptions.${item.key.replace(/[^a-zA-Z0-9]/g, '_')}`;
    return t.has(key) ? t(key) : item.description || '';
  };

  const renderMegaMenuItem = (item) => {
    const label = getMenuItemLabel(item);
    const FeatureIcon = item.icon ? MENU_ITEM_ICONS[item.icon] : null;

    return (
      <Link
        key={item.key}
        href={item.href}
        className="group flex min-h-[58px] gap-3 rounded-md px-2 py-2 transition-colors duration-150 hover:bg-white/[0.055] focus:outline-none focus-visible:bg-white/[0.055] focus-visible:ring-2 focus-visible:ring-amber-300/45"
        onClick={(event) => {
          setActiveMegaMenu(null);
          handleHeaderLinkClick(event);
        }}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/[0.055]">
          {item.logo ? (
            <Image
              src={item.logo}
              alt={`${label} logo`}
              width={24}
              height={24}
              className="h-6 w-6 object-contain"
              style={item.invertLogo || item.key.includes('grok') ? { filter: 'invert(1) brightness(1.18)' } : {}}
            />
          ) : FeatureIcon ? (
            <FeatureIcon className="h-5 w-5 text-amber-400" strokeWidth={1.9} />
          ) : null}
        </span>
        <span className="min-w-0 flex-1 pt-px">
          <span className="mb-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-semibold leading-5 text-neutral-100 group-hover:text-white">
              {label}
            </span>
            {(item.badges || (item.badge ? [item] : [])).map((badge) => (
              <span
                key={badge.badgeTone}
                className={`shrink-0 rounded-md px-1.5 py-[2px] text-[9px] font-semibold leading-none ${MODEL_MENU_BADGE_STYLES[badge.badgeTone] || ''}`}
              >
                {badge.badgeKey ? t(`badges.${badge.badgeKey}`) : badge.badge}
              </span>
            ))}
          </span>
          <span className="block text-xs leading-4 text-neutral-400 group-hover:text-neutral-300 min-h-8 line-clamp-2">
            {getMenuItemDescription(item)}
          </span>
        </span>
      </Link>
    );
  };

  const renderMegaMenuSection = (title, items) => (
    <section>
      <h3 className="mb-2 px-2 text-[11px] font-semibold uppercase text-neutral-500">{title}</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(renderMegaMenuItem)}
      </div>
    </section>
  );

  const renderMegaMenuPanel = () => {
    const menu = activeMegaMenu ? megaMenus[activeMegaMenu] : null;
    if (!menu) return null;

    return (
      <div
        className="absolute top-full -mt-2 w-[min(960px,calc(100vw-2rem))]"
        style={{ left: `${megaMenuLeft}px` }}
        onMouseEnter={clearMegaMenuCloseTimer}
        onMouseLeave={scheduleMegaMenuClose}
      >
        <div className="relative overflow-hidden rounded-lg border border-white/[0.09] bg-[#1c1917] shadow-[0_24px_70px_rgba(0,0,0,0.62)]">
          {/* 整面板滚动：高度在原 690px 基础上再加半行模型高度（约 38px）= 728px，
              让下一行露出半截作为"下面还有内容"的暗示（content peek），
              滚动条完全隐藏，底部渐隐遮罩随滚动状态隐现；矮视口按 100vh-7rem 收缩。 */}
          <div
            ref={megaMenuScrollRef}
            onScroll={updateMegaMenuFade}
            className="header-mega-scroll max-h-[min(728px,calc(100vh-7rem))] space-y-4 overflow-y-auto px-4 py-4"
          >
            {renderMegaMenuSection(t('megaMenu.sections.features'), menu.features)}
            {renderMegaMenuSection(t('megaMenu.sections.models'), menu.models)}
          </div>
          {/* 底部渐隐遮罩：还有未显示内容时淡入，滚到底自动消失 */}
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-[linear-gradient(to_top,rgba(28,25,23,0.96)_0%,rgba(28,25,23,0.55)_45%,transparent_100%)] transition-opacity duration-200 ${megaMenuHasMore ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>
      </div>
    );
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md"
      style={{ backgroundColor: 'rgba(var(--background-start-rgb), 0.58)' }}
      onMouseEnter={clearMegaMenuCloseTimer}
      onMouseLeave={scheduleMegaMenuClose}
    >
      <SiteAnnouncementBar
        config={siteAnnouncement}
        pathname={pathname}
        onAction={handleAnnouncementAction}
      />

      <div className="container mx-auto px-4 py-3 flex items-center">
        <div className="flex items-center">
          <Link href="/" onClick={handleHeaderLinkClick} className="flex items-center mr-8">
            <BrandLockup animated alt="BotGo Home" hoverExpression="focus" priority size={40} />
          </Link>

          <nav className="hidden lg:flex items-center space-x-2">
            {socialNavEnabled && (
              <>
                <Link
                  href="/explore"
                  onClick={handleHeaderLinkClick}
                  onMouseEnter={() => {
                    clearMegaMenuCloseTimer();
                    setActiveMegaMenu(null);
                  }}
                  className="inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium text-gray-300 transition-colors hover:bg-white/[0.04] hover:text-amber-300"
                >
                  {t('explore')}
                </Link>
                <Link
                  href="/create"
                  onClick={handleHeaderLinkClick}
                  onMouseEnter={() => {
                    clearMegaMenuCloseTimer();
                    setActiveMegaMenu(null);
                  }}
                  className="inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium text-gray-300 transition-colors hover:bg-white/[0.04] hover:text-amber-300"
                >
                  {t('create')}
                </Link>
              </>
            )}
            <button
              ref={videoMenuTriggerRef}
              type="button"
              onMouseEnter={(event) => openMegaMenu('video', event.currentTarget)}
              onFocus={(event) => openMegaMenu('video', event.currentTarget)}
              onClick={(event) => openMegaMenu('video', event.currentTarget)}
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-all ${activeMegaMenu === 'video'
                ? 'bg-white/[0.07] text-amber-400'
                : 'text-gray-300 hover:bg-white/[0.04] hover:text-amber-400'
                }`}
              aria-expanded={activeMegaMenu === 'video'}
            >
              <span>AI {t('video')}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${activeMegaMenu === 'video' ? 'rotate-180' : ''}`} />
            </button>

            <button
              ref={imageMenuTriggerRef}
              type="button"
              onMouseEnter={(event) => openMegaMenu('image', event.currentTarget)}
              onFocus={(event) => openMegaMenu('image', event.currentTarget)}
              onClick={(event) => openMegaMenu('image', event.currentTarget)}
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-all ${activeMegaMenu === 'image'
                ? 'bg-white/[0.07] text-amber-400'
                : 'text-gray-300 hover:bg-white/[0.04] hover:text-amber-400'
                }`}
              aria-expanded={activeMegaMenu === 'image'}
            >
              <span>AI {t('image')}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${activeMegaMenu === 'image' ? 'rotate-180' : ''}`} />
            </button>

            <Link
              href="/pricing"
              onClick={handleHeaderLinkClick}
              onMouseEnter={() => {
                clearMegaMenuCloseTimer();
                setActiveMegaMenu(null);
              }}
              className="inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium text-gray-300 transition-colors hover:bg-white/[0.04] hover:text-amber-300"
            >
              {t('pricing')}
            </Link>
          </nav>
        </div>

        <div className="ml-auto flex items-center gap-6">
          <LanguageSwitcher />
          {isLoading ? (
            // 加载状态显示骨架
            <div className="flex items-center gap-4">
              {/* 对应 Bell 图标位置 */}
              <div className="hidden md:block">
                <Skeleton className="w-8 h-8 rounded-full" />
              </div>
              {/* 对应 Upgrade 按钮位置 */}
              <div className="hidden md:block">
                <Skeleton className="w-24 h-8 rounded-lg" />
              </div>
              {/* 对应 Avatar 位置 */}
              <Skeleton className="w-9 h-9 rounded-full" />
            </div>
          ) : user ? (
            <>
              {/* 通知铃铛图标和 Upgrade 按钮容器 */}
              <div className="hidden items-center gap-5 md:flex">
                {/* 通知铃铛图标 */}
                <button
                  type="button"
                  onClick={() => setNotificationsOpen(true)}
                  className="relative flex items-center justify-center w-8 h-8 rounded-full text-neutral-400 hover:text-amber-400 hover:bg-neutral-800 transition-all"
                  title={t('notifications')}
                  aria-label={t('notifications')}
                >
                  <Bell className="w-4 h-4" />
                  {notifications.unreadCount > 0 ? (
                    <span className="absolute -right-0.5 -top-0.5 grid min-h-4 min-w-4 place-items-center rounded-full bg-[#F6B51B] px-1 text-[9px] font-bold leading-none text-[#171006]">
                      {notifications.unreadCount > 99 ? '99+' : notifications.unreadCount}
                    </span>
                  ) : null}
                </button>

                {/* Upgrade + 积分下拉菜单 */}
                <DropdownMenu open={isUpgradeDropdownOpen} onOpenChange={setIsUpgradeDropdownOpen} modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button
                      onMouseEnter={() => {
                        upgradeMenuPointerRef.current = true;
                        openHoverMenu(setIsUpgradeDropdownOpen, upgradeMenuCloseTimerRef);
                      }}
                      onKeyDownCapture={() => { upgradeMenuPointerRef.current = false; }}
                      onMouseLeave={() => closeHoverMenu(setIsUpgradeDropdownOpen, upgradeMenuCloseTimerRef)}
                      className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 text-amber-400 px-3 py-1 rounded-lg text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
                      aria-label={t('creditsAndSubscribe')}
                    >
                      <span>{t('upgrade')}</span>
                      <Zap className="w-3.5 h-3.5" />
                      <span className="font-semibold">{credits}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onCloseAutoFocus={(event) => {
                      // Hover dismissal must not move focus back to the trigger.
                      if (upgradeMenuPointerRef.current) event.preventDefault();
                    }}
                    onMouseEnter={() => {
                      upgradeMenuPointerRef.current = true;
                      openHoverMenu(setIsUpgradeDropdownOpen, upgradeMenuCloseTimerRef);
                    }}
                    onKeyDownCapture={() => { upgradeMenuPointerRef.current = false; }}
                    onMouseLeave={() => closeHoverMenu(setIsUpgradeDropdownOpen, upgradeMenuCloseTimerRef)}
                    className="w-[22rem] bg-neutral-800 border-neutral-700 p-0"
                  >
                    {/* 第一行：套餐名称 + Upgrade 按钮 */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm font-semibold text-white">{planName}</span>
                      <button
                        onClick={() => {
                          setIsUpgradeDropdownOpen(false);
                          setIsPricingModalOpen(true);
                        }}
                        className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs font-semibold px-4 py-1.5 rounded-md transition-all"
                      >
                        {t('upgrade')}
                      </button>
                    </div>

                    {/* 第二行：分隔线 */}
                    <DropdownMenuSeparator className="bg-neutral-700 my-0" />

                    {/* 积分详情 - 重新组织布局 */}
                    <div className="space-y-2 px-4 py-3">
                      {/* Subscription Credits (Monthly) */}
                      <button type="button" onClick={openUsageSettings} className="w-full rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-left transition hover:scale-[1.01] hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50">
                        <span className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Zap className="h-3.5 w-3.5 text-blue-400" />
                            <span className="text-xs text-neutral-300">{t('subscriptionCredits')}</span>
                          </span>
                          <strong className="text-xs font-semibold text-blue-400">{creditDetails.monthlyCredits}</strong>
                        </span>
                        <span className="mt-1.5 flex items-center justify-between pl-5 text-[10px] text-neutral-500">
                          <span>{t('nextReset')}</span>
                          <span>{nextMonthlyReset ? getTimeUntilExpiry(nextMonthlyReset) : ''}</span>
                        </span>
                      </button>

                      {/* Permanent Credits */}
                      <button type="button" onClick={openUsageSettings} className="w-full rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-left transition hover:scale-[1.01] hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50">
                        <span className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Zap className="h-3.5 w-3.5 text-purple-400" />
                            <span className="text-xs text-neutral-300">{t('purchasedCredits')}</span>
                          </span>
                          <strong className="text-xs font-semibold text-purple-400">{creditDetails.permanentCredits}</strong>
                        </span>
                        <span className="mt-1.5 block pl-5 text-[10px] text-neutral-500">{t('neverExpire')}</span>
                      </button>

                      {/* Purchased credits and daily free credits are separate buckets. */}
                      <div className="my-2 border-t border-neutral-700" />

                      {/* Daily Free Credits */}
                      <button type="button" onClick={openUsageSettings} className="w-full rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-left transition hover:scale-[1.01] hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50">
                        <span className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-green-400" />
                            <span className="text-xs text-neutral-300">{t('dailyFreeCredits')}</span>
                          </span>
                          <strong className="text-xs font-semibold text-green-400">{creditDetails.freeDailyCredits}</strong>
                        </span>
                        <span className="mt-1.5 flex items-center justify-between pl-5 text-[10px] text-neutral-500">
                          <span>{t('nextReset')}</span>
                          <span>{getTimeUntilMidnight()}</span>
                        </span>
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* 用户菜单 - 仅显示头像，鼠标悬停触发 */}
              <DropdownMenu open={isUserDropdownOpen} onOpenChange={setIsUserDropdownOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    onMouseEnter={() => {
                      userMenuPointerRef.current = true;
                      openHoverMenu(setIsUserDropdownOpen, userMenuCloseTimerRef);
                    }}
                    onKeyDownCapture={() => { userMenuPointerRef.current = false; }}
                    onMouseLeave={() => closeHoverMenu(setIsUserDropdownOpen, userMenuCloseTimerRef)}
                    onClick={() => {
                      setIsUserDropdownOpen(false);
                      // A full document navigation avoids a rejected Next.js RSC fetch
                      // from leaving the development error overlay on screen.
                      window.location.assign(`/${locale}/profile`);
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-transparent transition-all hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
                    aria-label={t('openProfile')}
                    title={t('openProfile')}
                  >
                    <ProfileAvatar
                      src={userMenuAvatarSrc}
                      alt={t('avatar')}
                      size={36}
                      priority
                      className="grid h-9 w-9 place-items-center"
                      fallback={<span className="text-sm font-bold text-white">{user.email?.charAt(0).toUpperCase()}</span>}
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onCloseAutoFocus={(event) => {
                    // Hover dismissal must not move focus back to the trigger.
                    if (userMenuPointerRef.current) event.preventDefault();
                  }}
                  onMouseEnter={() => {
                    userMenuPointerRef.current = true;
                    openHoverMenu(setIsUserDropdownOpen, userMenuCloseTimerRef);
                  }}
                  onKeyDownCapture={() => { userMenuPointerRef.current = false; }}
                  onMouseLeave={() => closeHoverMenu(setIsUserDropdownOpen, userMenuCloseTimerRef)}
                  className="w-64 bg-neutral-800 border-neutral-700"
                >
                  {/* 用户信息 */}
                  <div className="px-2 py-2">
                    <p className="text-sm font-medium text-white">
                      {userMenuDisplayName}
                    </p>
                    <p className="text-xs text-neutral-400 mt-0.5">{user.email}</p>
                  </div>

                  <DropdownMenuSeparator className="bg-neutral-700" />

                  {/* 菜单项 */}
                  <DropdownMenuItem asChild className="flex items-center gap-2 text-neutral-300 hover:text-white cursor-pointer">
                    <Link href="/profile"><User className="w-4 h-4" /><span>{t('profile')}</span></Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setSettingsInitialTab('account');
                      setIsSettingsModalOpen(true);
                    }}
                    className="flex items-center gap-2 text-neutral-300 hover:text-white cursor-pointer"
                  >
                    <Settings className="w-4 h-4" />
                    <span>{t('settings')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.preventDefault();
                      setExpandedUserMenuSection((current) => current === 'contact' ? null : 'contact');
                    }}
                    className="flex cursor-pointer items-center gap-2 text-neutral-300 focus:bg-neutral-700 focus:text-white"
                  >
                    <Contact className="h-4 w-4" />
                    <span className="flex-1">{t('contactUs')}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${expandedUserMenuSection === 'contact' ? 'rotate-180' : ''}`}
                    />
                  </DropdownMenuItem>
                  {expandedUserMenuSection === 'contact' ? (
                    <div className="mb-1 ml-6 border-l border-white/10 pl-2">
                      <DropdownMenuItem asChild className="cursor-pointer text-neutral-400 focus:bg-neutral-700 focus:text-white">
                        <a href="mailto:support@botgo.ai"><Mail className="h-4 w-4" />support@botgo.ai</a>
                      </DropdownMenuItem>
                    </div>
                  ) : null}
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.preventDefault();
                      setExpandedUserMenuSection((current) => current === 'policies' ? null : 'policies');
                    }}
                    className="flex cursor-pointer items-center gap-2 text-neutral-300 focus:bg-neutral-700 focus:text-white"
                  >
                    <FileText className="h-4 w-4" />
                    <span className="flex-1">{t('termsPolicies')}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${expandedUserMenuSection === 'policies' ? 'rotate-180' : ''}`}
                    />
                  </DropdownMenuItem>
                  {expandedUserMenuSection === 'policies' ? (
                    <div className="mb-1 ml-6 border-l border-white/10 pl-2">
                      <DropdownMenuItem asChild className="cursor-pointer text-neutral-400 focus:bg-neutral-700 focus:text-white">
                        <Link href="/terms-of-service"><FileText className="h-4 w-4" />{t('termsOfService')}</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer text-neutral-400 focus:bg-neutral-700 focus:text-white">
                        <Link href="/privacy-policy"><ShieldCheck className="h-4 w-4" />{t('privacyPolicy')}</Link>
                      </DropdownMenuItem>
                    </div>
                  ) : null}
                  <DropdownMenuItem
                    onClick={() => setIsInviteModalOpen(true)}
                    className="flex items-center gap-2 text-neutral-300 hover:text-white cursor-pointer"
                  >
                    <UserPlus className="w-4 h-4" /><span>{t('inviteFriends')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled className="flex items-center gap-2 text-neutral-500">
                    <Moon className="w-4 h-4" /><span>{t('theme')}</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-neutral-700" />

                  {/* 退出登录 */}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-amber-400 hover:text-error-400 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t('signOut')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-1.5 px-4 rounded-lg text-sm transition-all"
              >
                {t('signIn')}
              </button>
            </>
          )}

          {/* Google One Tap - 仅在未登录且加载完成时显示 */}
          {!user && !isLoading && <GoogleOneTap />}
        </div>
      </div >

      {renderMegaMenuPanel()}

      <Suspense fallback={null}>
        <HeaderUrlModals
          supabase={supabase}
          setIsAuthModalOpen={setIsAuthModalOpen}
          setRedirectPath={setRedirectPath}
          setSettingsInitialTab={setSettingsInitialTab}
          setIsSettingsModalOpen={setIsSettingsModalOpen}
        />
      </Suspense>

      {/* 认证弹窗 */}
      < AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false);
          setRedirectPath(null);
        }
        }
        redirectPath={redirectPath}
      />

      {/* 价格套餐弹窗 */}
      < PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />

      {/* Settings 弹窗 */}
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        initialTab={settingsInitialTab}
      />

      {/* 邀请好友弹窗 */}
      <InviteFriendsModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      <NotificationPanel open={notificationsOpen} onClose={() => setNotificationsOpen(false)} center={notifications} side="right" />

      <PersonalizedOfferModal
        config={personalizedOffer}
        pathname={pathname}
        onClaim={() => setIsPricingModalOpen(true)}
      />
    </header >
  );
} 
